/**
 * Client-Side Production-Safe Logger
 * Browser-optimized logging with sensitive data protection
 */

export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3,
  TRACE = 4
}

interface LogContext {
  [key: string]: any;
}

class ClientLogger {
  private static instance: ClientLogger;
  private logLevel: LogLevel;
  private isProduction: boolean;
  private isDevelopment: boolean;
  private sensitivePatterns: RegExp[];
  private logBuffer: any[] = [];
  private maxBufferSize = 100;

  private constructor() {
    this.isProduction = import.meta.env?.PROD || process.env.NODE_ENV === 'production';
    this.isDevelopment = import.meta.env?.DEV || process.env.NODE_ENV === 'development';
    
    // Set log level based on environment
    this.logLevel = this.getLogLevelFromEnv();
    
    // Patterns to detect sensitive data
    this.sensitivePatterns = [
      /^Bearer\s+.+/i,
      /^Basic\s+.+/i,
      /password['":\s]*['"]\S+['"]/gi,
      /token['":\s]*['"]\S+['"]/gi,
      /api[_-]?key['":\s]*['"]\S+['"]/gi,
      /secret['":\s]*['"]\S+['"]/gi,
      /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, // Email
      /\b\d{3}-\d{2}-\d{4}\b/g, // SSN pattern
      /\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g, // Credit card
    ];

    // In production, override console methods
    if (this.isProduction) {
      this.overrideConsoleMethods();
    }
  }

  static getInstance(): ClientLogger {
    if (!ClientLogger.instance) {
      ClientLogger.instance = new ClientLogger();
    }
    return ClientLogger.instance;
  }

  private getLogLevelFromEnv(): LogLevel {
    // Check localStorage for runtime configuration
    const storedLevel = typeof window !== 'undefined' 
      ? localStorage.getItem('LOG_LEVEL')?.toUpperCase()
      : null;
    
    // Production defaults to ERROR only
    if (this.isProduction && !storedLevel) {
      return LogLevel.ERROR;
    }
    
    // Development defaults to DEBUG
    if (this.isDevelopment && !storedLevel) {
      return LogLevel.DEBUG;
    }
    
    // Parse stored level
    switch (storedLevel) {
      case 'ERROR': return LogLevel.ERROR;
      case 'WARN': return LogLevel.WARN;
      case 'INFO': return LogLevel.INFO;
      case 'DEBUG': return LogLevel.DEBUG;
      case 'TRACE': return LogLevel.TRACE;
      default: return this.isProduction ? LogLevel.ERROR : LogLevel.INFO;
    }
  }

  private shouldLog(level: LogLevel): boolean {
    return level <= this.logLevel;
  }

  private sanitizeString(str: string): string {
    let sanitized = str;
    for (const pattern of this.sensitivePatterns) {
      sanitized = sanitized.replace(pattern, '[REDACTED]');
    }
    return sanitized;
  }

  private sanitizeData(data: any, depth = 0): any {
    if (depth > 5) return '[Max Depth]';
    
    if (data === null || data === undefined) {
      return data;
    }
    
    if (typeof data === 'string') {
      return this.sanitizeString(data);
    }
    
    if (typeof data === 'object') {
      if (data instanceof Error) {
        return {
          name: data.name,
          message: this.sanitizeString(data.message),
          ...(this.isDevelopment && { stack: this.sanitizeString(data.stack || '') })
        };
      }
      
      if (Array.isArray(data)) {
        return data.map(item => this.sanitizeData(item, depth + 1));
      }
      
      const sanitized: LogContext = {};
      for (const [key, value] of Object.entries(data)) {
        const lowerKey = key.toLowerCase();
        const isSensitive = ['password', 'token', 'secret', 'key', 'auth', 'cookie', 'session']
          .some(word => lowerKey.includes(word));
        
        if (isSensitive) {
          sanitized[key] = '[REDACTED]';
        } else {
          sanitized[key] = this.sanitizeData(value, depth + 1);
        }
      }
      return sanitized;
    }
    
    return data;
  }

  private addToBuffer(entry: any): void {
    this.logBuffer.push(entry);
    if (this.logBuffer.length > this.maxBufferSize) {
      this.logBuffer.shift();
    }
  }

  private formatMessage(level: string, message: string, context?: LogContext): any {
    const timestamp = new Date().toISOString();
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown';
    
    const logEntry = {
      timestamp,
      level,
      message: this.sanitizeString(message),
      ...(context && { context: this.sanitizeData(context) }),
      ...(this.isProduction && { userAgent })
    };

    // Store in buffer for potential error reporting
    this.addToBuffer(logEntry);
    
    return logEntry;
  }

  private overrideConsoleMethods(): void {
    const originalConsole = {
      log: console.log,
      error: console.error,
      warn: console.warn,
      info: console.info,
      debug: console.debug
    };

    // Override console.log - suppress in production
    console.log = (...args: any[]) => {
      if (this.shouldLog(LogLevel.DEBUG)) {
        originalConsole.log(...args.map(arg => this.sanitizeData(arg)));
      }
    };

    // Override console.error - always allow but sanitize
    console.error = (...args: any[]) => {
      if (this.shouldLog(LogLevel.ERROR)) {
        originalConsole.error(...args.map(arg => this.sanitizeData(arg)));
      }
    };

    // Override console.warn - allow based on level
    console.warn = (...args: any[]) => {
      if (this.shouldLog(LogLevel.WARN)) {
        originalConsole.warn(...args.map(arg => this.sanitizeData(arg)));
      }
    };

    // Override console.info - suppress in production
    console.info = (...args: any[]) => {
      if (this.shouldLog(LogLevel.INFO)) {
        originalConsole.info(...args.map(arg => this.sanitizeData(arg)));
      }
    };

    // Override console.debug - suppress in production
    console.debug = (...args: any[]) => {
      if (this.shouldLog(LogLevel.DEBUG)) {
        originalConsole.debug(...args.map(arg => this.sanitizeData(arg)));
      }
    };
  }

  error(message: string, error?: Error | any, context?: LogContext): void {
    if (!this.shouldLog(LogLevel.ERROR)) return;
    
    const logEntry = this.formatMessage('ERROR', message, {
      ...context,
      ...(error && { error: this.sanitizeData(error) })
    });
    
    console.error('[ERROR]', logEntry.message, logEntry.context);
  }

  warn(message: string, context?: LogContext): void {
    if (!this.shouldLog(LogLevel.WARN)) return;
    const logEntry = this.formatMessage('WARN', message, context);
    console.warn('[WARN]', logEntry.message, logEntry.context);
  }

  info(message: string, context?: LogContext): void {
    if (!this.shouldLog(LogLevel.INFO)) return;
    const logEntry = this.formatMessage('INFO', message, context);
    console.log('[INFO]', logEntry.message, logEntry.context);
  }

  debug(message: string, context?: LogContext): void {
    if (!this.shouldLog(LogLevel.DEBUG)) return;
    if (!this.isProduction) {
      const logEntry = this.formatMessage('DEBUG', message, context);
      console.log('[DEBUG]', logEntry.message, logEntry.context);
    }
  }

  trace(message: string, context?: LogContext): void {
    if (!this.shouldLog(LogLevel.TRACE)) return;
    if (!this.isProduction) {
      const logEntry = this.formatMessage('TRACE', message, context);
      console.log('[TRACE]', logEntry.message, logEntry.context);
    }
  }

  // Get buffered logs for error reporting
  getLogBuffer(): any[] {
    return [...this.logBuffer];
  }

  // Clear log buffer
  clearBuffer(): void {
    this.logBuffer = [];
  }

  // Update log level at runtime
  setLogLevel(level: LogLevel): void {
    this.logLevel = level;
    if (typeof window !== 'undefined') {
      localStorage.setItem('LOG_LEVEL', LogLevel[level]);
    }
  }
}

// Export singleton instance
export const logger = ClientLogger.getInstance();

// Export convenience functions
export const logError = (message: string, error?: Error | any, context?: LogContext) => 
  logger.error(message, error, context);

export const logWarn = (message: string, context?: LogContext) => 
  logger.warn(message, context);

export const logInfo = (message: string, context?: LogContext) => 
  logger.info(message, context);

export const logDebug = (message: string, context?: LogContext) => 
  logger.debug(message, context);

export const logTrace = (message: string, context?: LogContext) => 
  logger.trace(message, context);

// Development-only logger
export const devLog = (...args: any[]) => {
  if (import.meta.env?.DEV || process.env.NODE_ENV === 'development') {
    console.log('[DEV]', ...args);
  }
};